import z from "zod";

/**
 * What belongs in these schemas.
 *
 * A field the page reads is required: failing at the parse boundary beats an
 * `undefined` reaching a link or a label as a plausible-looking nothing. A
 * field the page does not read is left out rather than made optional — Zod
 * strips unknown keys, and a required field with no reader turns any rename of
 * it into a blank page, which is how this drill-down broke twice; optional
 * would stop the crash but keep the field in the inferred type for a reader to
 * depend on.
 *
 * The list is a claim about what the page depends on, not a description of the
 * response. Adding a reader means adding the field back.
 */

/**
 * The axis a breakdown groups on, in the API's own vocabulary rather than the
 * enum spelling the service uses internally.
 */
export const UsageDimensionSchema = z.enum(["day", "user", "project", "debit_kind", "activity", "type"]);
export type UsageDimension = z.infer<typeof UsageDimensionSchema>;

/**
 * A list field as this API can send it: present, JSON `null` from a Go nil
 * slice, or absent entirely when the field carries `omitempty`. All three mean
 * the same empty list to a reader.
 */
const listOf = <T extends z.ZodType>(item: T) =>
  z
    .array(item)
    .nullish()
    .transform((value) => value ?? []);

const usageRangeParams = {
  start: z.string(),
  end: z.string(),
};

/**
 * The narrowings every usage read accepts. The values are the keys a breakdown
 * groups on, so a filtered chart and the rows behind it answer the same
 * question.
 *
 * `filterType` is a bare string rather than an enum: the ledger's vocabulary is
 * open, and the server answers a value it has never written with an empty page
 * rather than an error.
 */
const usageFilterParams = {
  filterUser: z.string().optional(),
  filterProject: z.string().optional(),
  filterType: z.string().optional(),
};

export const UsageSortOrderSchema = z.enum(["asc", "desc"]);
export type UsageSortOrder = z.infer<typeof UsageSortOrderSchema>;

export const UsageSummaryParamsSchema = z.object(usageRangeParams);

export const UsageBreakdownParamsSchema = z.object({
  ...usageRangeParams,
  ...usageFilterParams,
  dimension: UsageDimensionSchema,
  subDimension: UsageDimensionSchema.optional(),
});

export const UsageDebitsSortSchema = z.enum(["occurred_at", "credits", "cost"]);
export type UsageDebitsSort = z.infer<typeof UsageDebitsSortSchema>;

export const UsageDebitsParamsSchema = z.object({
  ...usageRangeParams,
  ...usageFilterParams,
  dimension: UsageDimensionSchema,
  key: z.string(),
  subDimension: UsageDimensionSchema.optional(),
  subKey: z.string().optional(),
  sort: UsageDebitsSortSchema.optional(),
  order: UsageSortOrderSchema.optional(),
  afterId: z.string().optional(),
  limit: z.number().int().optional(),
});

/**
 * The spend on one cell of a breakdown.
 *
 * `sub_key` is dropped from the payload when it is empty, which happens both
 * when no second axis was asked for and when a debit carries no value on that
 * axis, so it lands back as the empty string either way.
 *
 * `label` is what to call a key that is an id. Only the by-user breakdown sends
 * one, and it is absent for a user who no longer exists, so a reader falls back
 * to the key. It comes from the server rather than a roster the browser holds:
 * someone granted a single project inside the organization spends its credits
 * without being one of its members.
 */
export const UsageGroupSchema = z.object({
  key: z.string(),
  sub_key: z.string().optional().default(""),
  label: z.string().optional().default(""),
  debits: z.number(),
  credits: z.number(),
  cost_millicents: z.number(),
});
export type UsageGroup = z.infer<typeof UsageGroupSchema>;

export const UsageBreakdownResponseSchema = z.object({
  data: listOf(UsageGroupSchema),
});

export const UsageCreditExpirySchema = z.object({
  expires_at: z.string(),
  credits: z.number(),
});
export type UsageCreditExpiry = z.infer<typeof UsageCreditExpirySchema>;

export const UsageCreditGrantSchema = z.object({
  added_at: z.string(),
  expires_at: z.string(),
  credits: z.number(),
  credit_type: z.string(),
  description: z.string().optional().default(""),
});
export type UsageCreditGrant = z.infer<typeof UsageCreditGrantSchema>;

export const UsageScheduledRefillSchema = z.object({
  scheduled_for: z.string(),
  credits: z.number(),
  refill_type: z.string(),
});
export type UsageScheduledRefill = z.infer<typeof UsageScheduledRefillSchema>;

/** One plan of the subscription catalog. */
export const UsagePlanOptionSchema = z.object({
  plan: z.string(),
  name: z.string(),
  monthly_price_millicents: z.number(),
  monthly_credits: z.number(),
});

/**
 * The trailing month of spend weighed against the catalog.
 *
 * `recommended_plan` is absent whenever there is nothing to suggest, which
 * covers both an organization already on a plan that fits and one whose spend
 * outgrows every plan on offer. `current_plan` is absent when the catalog does
 * not price the plan kind the organization is on.
 */
export const UsagePlanFitSchema = z.object({
  current_plan: UsagePlanOptionSchema.optional(),
  recommended_plan: UsagePlanOptionSchema.optional(),
  credits_last_thirty_days: z.number(),
});
export type UsagePlanFit = z.infer<typeof UsagePlanFitSchema>;

export const UsageSummaryResponseSchema = z.object({
  credits_remaining: z.number(),
  credits_spent: z.number(),
  mean_daily_spend: z.number(),
  mean_daily_spend_window_days: z.number(),
  run_out_date: z.string().optional(),
  period_end: z.string(),
  projected_period_credits: z.number(),
  credits_to_buy: z.number(),
  expiring_credits: listOf(UsageCreditExpirySchema),
  scheduled_refills: listOf(UsageScheduledRefillSchema),
  grants: listOf(UsageCreditGrantSchema),
  plan_fit: UsagePlanFitSchema.optional(),
});
export type UsageSummaryResponse = z.infer<typeof UsageSummaryResponseSchema>;

/**
 * One ledger row behind a breakdown cell — one charge, on its own line. A turn
 * writes a row for its prompt and another for its response, and both appear.
 *
 * `debit_id` is the leaf the charge was for — a message, an asset, a live
 * session — and is the only id a link may target. The wire also carries `id`,
 * naming the ledger entry itself; a link built from that one looks right and
 * resolves to nothing, so it is left out rather than guarded.
 *
 * `debit_target` names what `debit_id` points at and `resource_id` the thing it
 * rolls up to: a prompt targets a message inside a chat, so a link goes to the
 * chat. `resource_kind` says what that thing is and `resource_origin` how it
 * came to be, which together are what the row's type reads; `resource_origin_id`
 * names what made it, and is where a live session's charge has to lead, while
 * `resource_title` is what to call it. The resource fields are absent on a
 * charge written before the ledger recorded them, and every vocabulary here is
 * open, so an unknown value renders as plain text rather than dropping the row.
 *
 * `resource_title` is also absent once the thing it named has been deleted,
 * which is not the same as the charge never having named one: the ledger
 * outlives what it charged for. It is also withheld when `private` is set: the
 * charge rolls up to a chat the viewer cannot read, so there is nothing to name
 * and nowhere to link.
 */
export const UsageDebitSchema = z.object({
  debit_id: z.string(),
  debit_kind: z.string(),
  debit_target: z.string(),
  resource_id: z.string().optional().default(""),
  resource_kind: z.string().optional().default(""),
  resource_origin: z.string().optional().default(""),
  resource_origin_id: z.string().optional().default(""),
  resource_title: z.string().optional().default(""),
  private: z.boolean().optional().default(false),
  project_id: z.string(),
  occurred_at: z.string(),
  credits: z.number(),
  cost_millicents: z.number(),
});
export type UsageDebit = z.infer<typeof UsageDebitSchema>;

/**
 * The item's own name is missing on purpose: titles are resolved after the page
 * is cut, so the server has no column to order by.
 */
export const UsageItemsSortSchema = z.enum(["recent", "credits", "cost", "type"]);
export type UsageItemsSort = z.infer<typeof UsageItemsSortSchema>;

export const UsageItemsParamsSchema = z.object({
  ...usageRangeParams,
  ...usageFilterParams,
  sort: UsageItemsSortSchema.optional(),
  order: UsageSortOrderSchema.optional(),
  afterId: z.string().optional(),
  limit: z.number().int().optional(),
});

/**
 * One chat, live session, or ingested asset with everything the plan spent on
 * it inside the range.
 *
 * `kind` and `origin` are open vocabularies, so neither is an enum here: a
 * value this build has not heard of has to reach the row as plain text, and a
 * closed enum would fail the parse and drop the whole page. `title` is absent
 * once the thing it names has been deleted, because the ledger outlives it.
 *
 * `recorded` is false where the charges never named what they were for, which is
 * every charge older than the ledger's resource columns. Such an item is the
 * single message it was charged against and never had a title. `exists` says the
 * thing is still there: a live session is stored unnamed until it is summarised,
 * so an absent `title` on its own does not mean it was deleted. `private` marks
 * a chat that is still there but that the viewer cannot read: the server
 * withholds its title, and a link to it would only 404.
 *
 * `project_id` is the project of the item's most recent charge, not of all of
 * them: a chat or an asset can move between projects and the ledger keeps the
 * charges written on either side, so an item's spend can span two projects
 * while the row points at one.
 */
export const UsageItemSchema = z.object({
  id: z.string(),
  kind: z.string(),
  origin: z.string().optional().default(""),
  title: z.string().optional().default(""),
  recorded: z.boolean().optional().default(false),
  exists: z.boolean().optional().default(false),
  private: z.boolean().optional().default(false),
  project_id: z.string().optional().default(""),
  credits: z.number(),
  cost_millicents: z.number(),
  last_charged_at: z.string(),
});
export type UsageItem = z.infer<typeof UsageItemSchema>;

export const UsageItemsResponseSchema = z.object({
  data: listOf(UsageItemSchema),
  last_id: z.string().optional().default(""),
  has_more: z.boolean(),
});

export const UsageDebitsResponseSchema = z.object({
  data: listOf(UsageDebitSchema),
  last_id: z.string().optional().default(""),
  has_more: z.boolean(),
});
